<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { computed } from 'vue';

type Props = {
  min?: number;
  max?: number;
  step?: number;
  modelValue: number;
};

// define component props for the slider component
const props = defineProps<Props>();

// define emits for the slider component
const emit = defineEmits(['update:modelValue']);

// define refs for the slider component
const sliderValue = ref(props.modelValue);
const slider = ref<any>(null);

// function to get the progress of the slider
const getProgress = (value: number, min: number, max: number) => {
  return ((value - min) / (max - min)) * 100;
};

// function to set the css variable for the progress
const setCSSProgress = (progress: number) => {
  slider.value.style.setProperty('--ProgressPercent', `${progress}%`);
};

const onInputChange = (e: Event) => {
  const sliderValue = (e.target as HTMLInputElement).value;

  emit('update:modelValue', sliderValue);
  changeProgress();
};

onMounted(() => {
  changeProgress();
});

watch(
  () => props.modelValue,
  () => {
    sliderValue.value = props.modelValue;
    if (props.max && props.modelValue > props.max) {
      sliderValue.value = props.max;
    }
    if (props.min && props.modelValue < props.min) {
      sliderValue.value = props.min;
    }
    changeProgress();
  },
);

const changeProgress = () => {
  const progress = getProgress(sliderValue.value, slider.value.min, slider.value.max);

  // define extrawidth to ensure that the end of progress is always under the slider thumb.
  const extraWidth = (100 - progress) / 10;

  // set the css variable
  setCSSProgress(progress + extraWidth);
};
</script>
<template>
  <div class="custom-slider">
    <input
      ref="slider"
      :value="sliderValue"
      type="range"
      :min="min"
      :max="max"
      :step="step"
      class="slider"
      @input="onInputChange" />
  </div>
</template>
<style scoped lang="postcss">
.custom-slider {
  --trackHeight: 2px;
  --thumbRadius: 1rem;
  display: flex;
}

/* style the input element with type "range" */
.custom-slider input[type='range'] {
  position: relative;
  appearance: none;
  background: none;
  border-radius: 999px;
  z-index: 0;
  height: 2px;
  width: 71px;
}

/* ::before element to replace the slider track */
.custom-slider input[type='range']::before,
.custom-slider.minmax::before {
  content: '';
  position: absolute;
  width: var(--ProgressPercent, 100%);
  height: 100%;
  background: theme('colors.primary.300');
  /* z-index: -1; */
  pointer-events: none;
  border-radius: 999px;
}

/* `::-webkit-slider-runnable-track` targets the track (background) of a range slider in chrpme and safari browsers. */
.custom-slider input[type='range']::-webkit-slider-runnable-track {
  appearance: none;
  background: #494949;
  height: var(--trackHeight);
  border-radius: 999px;
}

/* `::-moz-range-track` targets the track (background) of a range slider in Mozilla Firefox. */
.custom-slider input[type='range']::-moz-range-track {
  appearance: none;
  background: theme('colors.primary.300');
  height: var(--trackHeight);
  border-radius: 999px;
}

.custom-slider input[type='range']::-webkit-slider-thumb {
  position: relative;
  width: var(--thumbRadius);
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  background: white;
  border-radius: 999px;
  pointer-events: all;
  cursor: pointer;
  appearance: none;
  z-index: 1;
}
</style>
